import {
    basicInfoSchema,
    dataStoresSchema,
    environmentsSchema,
    environmentBasicSchema,
    environmentDataSourcesSchema,
    environmentVirtualDirsSchema,
    environmentAnalyticsSchema,
    environmentSecuritySchema,
    environmentSecurityGeneralSchema,
    environmentSecurityCSPSchema,
    environmentSecurityHSTSSchema,
    environmentSecurityCORSSchema,
    analyticsSchema,
    authorizationSchema,
    securitySchema,
} from './TabbedSettingsSchemas';

export interface TabData {
    key: string;
    title: string;
    description: string;
    schema: any;
    uiSchema?: any;
    subTabs?: TabData[];
}

// Create environment-specific CSP sub-tabs dynamically
export const createEnvironmentCSPSubTabs = (environments: any[]): TabData[] => {
    return environments.map((env) => ({
        key: `environments-security-csp-${env.id}`,
        title: env.id.toUpperCase(),
        description: `Configure Content Security Policy settings for ${env.id} environment`,
        schema: {
            type: 'object',
            title: `${env.id.toUpperCase()} - Content Security Policy`,
            description: `Configure Content Security Policy settings for ${env.id} environment`,
            properties: {
                environments: {
                    type: 'array',
                    title: 'Environments',
                    items: {
                        type: 'object',
                        properties: {
                            id: {
                                type: 'string',
                                const: env.id,
                                title: 'Environment ID',
                                readOnly: true,
                            },
                            securitySettings: {
                                type: 'object',
                                title: 'Content Security Policy',
                                properties: {
                                    csp: {
                                        type: 'object',
                                        title: 'Content Security Policy',
                                        properties: {
                                            defaultSrc: {
                                                type: 'string',
                                                title: 'Default Source',
                                                description:
                                                    'The default-src is the default policy for loading content such as JavaScript, Images, CSS, Fonts, AJAX requests, Frames, HTML5 Media.',
                                            },
                                            scriptSrc: {
                                                type: 'string',
                                                title: 'Script Source',
                                                description:
                                                    'The script-src directive restricts the locations from which a script can be executed.',
                                            },
                                            scriptSrcAttr: {
                                                type: 'string',
                                                title: 'Script Source Attribute',
                                                description:
                                                    'The script-src-attr directive restricts the locations from which a script can be executed.',
                                            },
                                            styleSrc: {
                                                type: 'string',
                                                title: 'Style Source',
                                                description:
                                                    'The style-src directive restricts the locations from which a style sheet can be applied.',
                                            },
                                            imgSrc: {
                                                type: 'string',
                                                title: 'Image Source',
                                                description:
                                                    'The img-src directive restricts the locations from which images can be loaded.',
                                            },
                                            connectSrc: {
                                                type: 'string',
                                                title: 'Connect Source',
                                                description:
                                                    'The connect-src directive restricts the locations which can be loaded using script interfaces.',
                                            },
                                            fontSrc: {
                                                type: 'string',
                                                title: 'Font Source',
                                                description:
                                                    'The font-src directive restricts the locations from which fonts can be loaded.',
                                            },
                                            objectSrc: {
                                                type: 'string',
                                                title: 'Object Source',
                                                description:
                                                    'The object-src directive restricts the locations from which plugins can be embedded.',
                                            },
                                            mediaSrc: {
                                                type: 'string',
                                                title: 'Media Source',
                                                description:
                                                    'The media-src directive restricts the locations from which media can be loaded.',
                                            },
                                            frameSrc: {
                                                type: 'string',
                                                title: 'Frame Source',
                                                description:
                                                    'The frame-src directive restricts the locations from which the protected resource can embed frames.',
                                            },
                                        },
                                    },
                                },
                            },
                        },
                        required: ['id'],
                    },
                },
            },
        },
    }));
};

// Define base tabs structure
export const getTabsConfiguration = (environments: any[] = []): TabData[] => {
    return [
        {
            key: 'basic',
            title: 'Basic Info',
            description:
                'Configure basic site information including client details, hostnames, and favicon settings.',
            schema: basicInfoSchema,
        },
        {
            key: 'dataStores',
            title: 'Data Stores',
            description:
                'Manage data storage locations, caching settings, and file system configurations.',
            schema: dataStoresSchema,
        },
        {
            key: 'environments',
            title: 'Environments',
            description:
                'Configure deployment environments (dev, staging, production) with environment-specific settings and data sources.',
            schema: environmentsSchema,
            subTabs: [
                {
                    key: 'environments-basic',
                    title: 'Basic',
                    description: 'Configure basic environment settings including ID and path',
                    schema: environmentBasicSchema,
                },
                {
                    key: 'environments-datasources',
                    title: 'Data Sources',
                    description: 'Configure environment-specific data source mappings',
                    schema: environmentDataSourcesSchema,
                },
                {
                    key: 'environments-virtualdirs',
                    title: 'Virtual Directories',
                    description: 'Configure virtual directory mappings for each environment',
                    schema: environmentVirtualDirsSchema,
                },
                {
                    key: 'environments-analytics',
                    title: 'Analytics',
                    description: 'Configure analytics settings for each environment',
                    schema: environmentAnalyticsSchema,
                },
                {
                    key: 'environments-security',
                    title: 'Security',
                    description: 'Configure security settings for each environment',
                    schema: environmentSecuritySchema,
                    subTabs: [
                        {
                            key: 'environments-security-general',
                            title: 'General',
                            description:
                                'Configure general security policies including cross-origin and referrer policies',
                            schema: environmentSecurityGeneralSchema,
                        },
                        {
                            key: 'environments-security-csp',
                            title: 'CSP',
                            description:
                                'Configure Content Security Policy directives by environment',
                            schema: environmentSecurityCSPSchema,
                            subTabs: createEnvironmentCSPSubTabs(environments),
                        },
                        {
                            key: 'environments-security-hsts',
                            title: 'HSTS',
                            description: 'Configure HTTP Strict Transport Security settings',
                            schema: environmentSecurityHSTSSchema,
                        },
                        {
                            key: 'environments-security-cors',
                            title: 'CORS',
                            description: 'Configure Cross Origin Resource Sharing settings',
                            schema: environmentSecurityCORSSchema,
                        },
                    ],
                },
            ],
        },
        {
            key: 'analytics',
            title: 'Analytics',
            description:
                'Set up analytics tracking with Google Analytics, Adobe Experience Manager, or other platforms.',
            schema: analyticsSchema,
        },
        {
            key: 'authorization',
            title: 'Authorization',
            description:
                'Configure access control, IP whitelisting, and other security authorization settings.',
            schema: authorizationSchema,
        },
        {
            key: 'security',
            title: 'Security',
            description:
                'Manage global security settings including CORS, CSP, HSTS, and other security headers.',
            schema: securitySchema,
        },
    ];
};
